import { aiOpenapiClient } from '@/api/openapi-client';
import { env } from '@/env';
import { useCanUseAi } from '@/features/ai-assistant/hooks/useCanUseAi';
import {
  useAiChangeset,
  type AiChangesetAdapter,
} from '@/features/ai-changeset/hooks/useAiChangeset';
import { changesetApi } from '@/features/ai-changeset/utils/changeset-api';
import { lastDraft } from '@/features/ai-changeset/utils/last-draft';
import {
  buildExceptionsDraft,
  exceptionChangeLabels,
  summarizeExceptionsDraft,
} from '@/features/opening-hours/utils/exception-draft';
import { useT } from '@/i18n';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type { Exception, ExceptionChange } from '@restaurant/api-contracts';
import {
  ExceptionChangesetStaleSchema,
  ExceptionChangesSchema,
  ExceptionsChangesetAppliedSchema,
  ExceptionsQuickEditSessionSchema,
} from '@restaurant/api-contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

export function useExceptionsQuickEdit(exceptions: Exception[] | undefined) {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const canUseAi = useCanUseAi();
  const summaryCopy = useT().exceptionsQuickEdit.summary;
  const qc = useQueryClient();
  const [draftOf] = useState(() => lastDraft(buildExceptionsDraft));
  const params = { restaurantId };

  const adapter = useMemo<AiChangesetAdapter<ExceptionChange>>(
    () => ({
      targets: (changes) => (exceptions ? draftOf(exceptions, changes).targets : []),
      labels: (changes) => (exceptions ? exceptionChangeLabels(exceptions, changes) : []),
      summary: (changes) => summarizeExceptionsDraft(changes, summaryCopy),
    }),
    [exceptions, draftOf, summaryCopy],
  );

  const quickEdit = useAiChangeset<ExceptionChange>({
    changesSchema: ExceptionChangesSchema,
    id: `exceptions:${restaurantId}`,
    enabled: canUseAi && !!restaurantId && !!exceptions,
    turnUrl: `${env.aiUrl}/restaurants/${restaurantId}/ai/exceptions/quick-edit`,
    pageName: 'opening-hours-exceptions',
    fullViewLabel: null,
    viewRows: 5,
    api: changesetApi<ExceptionChange, unknown>(
      {
        sessionSchema: ExceptionsQuickEditSessionSchema,
        staleSchema: ExceptionChangesetStaleSchema,
        appliedSchema: ExceptionsChangesetAppliedSchema,
      },
      {
        load: (before) =>
          aiOpenapiClient.GET('/restaurants/{restaurantId}/ai/exceptions/quick-edit', {
            params: { path: params, query: { before } },
          }),
        apply: (turnId) =>
          aiOpenapiClient.POST(
            '/restaurants/{restaurantId}/ai/exceptions/quick-edit/turns/{turnId}/apply',
            { params: { path: { ...params, turnId } } },
          ),
        undo: (turnId) =>
          aiOpenapiClient.POST(
            '/restaurants/{restaurantId}/ai/exceptions/quick-edit/turns/{turnId}/undo',
            { params: { path: { ...params, turnId } } },
          ),
        discard: (turnId) =>
          aiOpenapiClient.POST(
            '/restaurants/{restaurantId}/ai/exceptions/quick-edit/turns/{turnId}/discard',
            { params: { path: { ...params, turnId } } },
          ),
      },
    ),
    onApplySettled: () => qc.invalidateQueries({ queryKey: ['exceptions', restaurantId] }),
    adapter,
  });

  return {
    ...quickEdit,
    draft: draftOf(exceptions ?? [], quickEdit.changes),
    canUseAi,
  };
}

export type ExceptionsQuickEdit = ReturnType<typeof useExceptionsQuickEdit>;
